import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// ============================================================================
// TYPES
// ============================================================================

export type DashboardLevel = 
  | 'business' 
  | 'product' 
  | 'products'
  | 'operations' 
  | 'development' 
  | 'projections' 
  | 'marketfit' 
  | 'stakeholders' 
  | 'shareholders'
  | 'employees'
  | 'community'
  | 'customers';

export interface DashboardLocation {
  level: DashboardLevel;
  artifactId?: string;
  artifactName?: string;
  productId?: string;
  productName?: string;
  subLevel?: string;
}

export interface BreadcrumbItem {
  label: string;
  location: DashboardLocation;
  path: string;
  icon?: string;
}

export interface DashboardFavorite {
  id: string;
  label: string;
  location: DashboardLocation;
  path: string;
  createdAt: string;
}

export interface DashboardNavigationState {
  currentLocation: DashboardLocation;
  breadcrumbs: BreadcrumbItem[];
  favorites: DashboardFavorite[];
  recentLocations: DashboardLocation[];
  isTransitioning: boolean;
}

export interface DashboardNavigationActions {
  // Navigation
  navigateTo: (location: DashboardLocation) => void;
  navigateToPath: (path: string) => void;
  goBack: () => void;
  goToLevel: (level: DashboardLevel, artifactId?: string, artifactName?: string) => void;
  
  // Drill-down actions
  drillDownToProduct: (productId: string, productName: string) => void;
  drillDownToOperations: (productId: string, productName: string) => void;
  drillDownToDevelopment: (productId: string, productName: string) => void;
  drillDownToProjections: (productId: string, productName: string) => void;
  drillDownToMarketFit: (productId: string, productName: string) => void;
  drillDownToStakeholders: () => void;
  drillDownToCustomers: () => void;
  
  // Favorites management
  addFavorite: (label: string, location?: DashboardLocation) => void;
  removeFavorite: (favoriteId: string) => void;
  isFavorite: (location: DashboardLocation) => boolean;
  toggleFavorite: (label?: string) => void;
  
  // Utility
  getLocationPath: (location: DashboardLocation) => string;
  getCurrentPath: () => string;
}

export interface DashboardNavigationContextType 
  extends DashboardNavigationState, 
          DashboardNavigationActions {}

// ============================================================================
// CONSTANTS
// ============================================================================

const FAVORITES_STORAGE_KEY = 'dashboard_favorites';
const RECENT_STORAGE_KEY = 'dashboard_recent';
const PRODUCT_NAMES_STORAGE_KEY = 'dashboard_product_names';
const MAX_RECENT_LOCATIONS = 10;

// Level hierarchy for breadcrumb generation
const LEVEL_HIERARCHY: Record<DashboardLevel, DashboardLevel | null> = {
  'business': null,
  'product': 'business',
  'products': 'business',
  'operations': 'product',
  'development': 'product',
  'projections': 'product',
  'marketfit': 'product',
  'stakeholders': 'business',
  'shareholders': 'stakeholders',
  'employees': 'stakeholders',
  'community': 'stakeholders',
  'customers': 'business',
};

const LEVEL_LABELS: Record<DashboardLevel, string> = {
  'business': 'Business Domain',
  'product': 'Product',
  'products': 'Products Overview',
  'operations': 'Operations',
  'development': 'Development',
  'projections': 'Projections',
  'marketfit': 'MarketFit',
  'stakeholders': 'Stakeholders',
  'shareholders': 'Shareholders',
  'employees': 'Employees',
  'community': 'Community',
  'customers': 'Customers',
};

const LEVEL_ICONS: Record<DashboardLevel, string> = {
  'business': 'dashboard',
  'product': 'product',
  'products': 'appstore',
  'operations': 'operations',
  'development': 'development',
  'projections': 'projections',
  'marketfit': 'marketfit',
  'stakeholders': 'team',
  'shareholders': 'shareholders',
  'employees': 'employees',
  'community': 'community',
  'customers': 'customers',
};

// ============================================================================
// CONTEXT
// ============================================================================

const DashboardNavigationContext = createContext<DashboardNavigationContextType | undefined>(undefined);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate path from location
 */
const getLocationPath = (location: DashboardLocation): string => {
  const { level, productId } = location;
  
  switch (level) {
    case 'business':
      return '/dashboard';
    case 'products':
      return '/dashboard/products';
    case 'product':
      return productId ? `/dashboard/product/${productId}` : '/dashboard';
    case 'operations':
      return productId ? `/dashboard/product/${productId}/operations` : '/dashboard';
    case 'development':
      return productId ? `/dashboard/product/${productId}/development` : '/dashboard';
    case 'projections':
      return productId ? `/dashboard/product/${productId}/projections` : '/dashboard';
    case 'marketfit':
      return productId ? `/dashboard/product/${productId}/marketfit` : '/dashboard';
    case 'stakeholders':
      return '/dashboard/stakeholders';
    case 'shareholders':
      return '/dashboard/stakeholders/shareholders';
    case 'employees':
      return '/dashboard/stakeholders/employees';
    case 'community':
      return '/dashboard/stakeholders/community';
    case 'customers':
      return '/dashboard/customers';
    default:
      return '/dashboard';
  }
};

/**
 * Parse location from URL path
 * @param path The URL path
 * @param existingLocation Optional existing location to preserve metadata like productName
 */
const parseLocationFromPath = (path: string, existingLocation?: DashboardLocation): DashboardLocation => {
  const parts = path.replace('/dashboard', '').split('/').filter(Boolean);
  
  if (parts.length === 0) {
    return { level: 'business' };
  }
  
  if (parts[0] === 'product' && parts[1]) {
    const productId = parts[1];
    const subPath = parts[2];
    
    // Try to preserve productName from existing location or localStorage
    const productName = existingLocation?.productId === productId 
      ? existingLocation.productName 
      : getStoredProductName(productId);
    
    const baseLocation = { productId, productName };
    
    switch (subPath) {
      case 'operations':
        return { level: 'operations', ...baseLocation };
      case 'development':
        return { level: 'development', ...baseLocation };
      case 'projections':
        return { level: 'projections', ...baseLocation };
      case 'marketfit':
        return { level: 'marketfit', ...baseLocation };
      default:
        return { level: 'product', ...baseLocation };
    }
  }
  
  if (parts[0] === 'products') {
    return { level: 'products' as DashboardLevel };
  }
  
  if (parts[0] === 'stakeholders') {
    const subPath = parts[1];
    switch (subPath) {
      case 'shareholders':
        return { level: 'shareholders' };
      case 'employees':
        return { level: 'employees' };
      case 'community':
        return { level: 'community' };
      default:
        return { level: 'stakeholders' };
    }
  }
  
  if (parts[0] === 'customers') {
    return { level: 'customers' };
  }
  
  return { level: 'business' };
};

/**
 * Generate breadcrumbs from location
 */
const generateBreadcrumbs = (location: DashboardLocation): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  let currentLevel: DashboardLevel | null = location.level;
  const locations: DashboardLocation[] = [];
  
  // Build the chain from current level to root
  while (currentLevel) {
    const loc: DashboardLocation = {
      level: currentLevel,
      productId: location.productId,
      productName: location.productName,
      artifactId: location.artifactId,
      artifactName: location.artifactName,
    };
    locations.unshift(loc);
    currentLevel = LEVEL_HIERARCHY[currentLevel];
  }
  
  // Convert to breadcrumb items
  for (const loc of locations) {
    let label = LEVEL_LABELS[loc.level];
    
    // Use custom names for product-level items
    if (loc.level === 'product' && loc.productName) {
      label = loc.productName;
    } else if (['operations', 'development', 'projections', 'marketfit'].includes(loc.level) && loc.productName) {
      // Keep default label for sub-levels, but include product context in location
    }
    
    breadcrumbs.push({
      label,
      location: loc,
      path: getLocationPath(loc),
      icon: LEVEL_ICONS[loc.level],
    });
  }
  
  return breadcrumbs;
};

/**
 * Load favorites from localStorage
 */
const loadFavorites = (): DashboardFavorite[] => {
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Save favorites to localStorage
 */
const saveFavorites = (favorites: DashboardFavorite[]): void => {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
  } catch (e) {
    console.error('Failed to save favorites:', e);
  }
};

/**
 * Load recent locations from localStorage
 */
const loadRecentLocations = (): DashboardLocation[] => {
  try {
    const stored = localStorage.getItem(RECENT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Save recent locations to localStorage
 */
const saveRecentLocations = (locations: DashboardLocation[]): void => {
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(locations.slice(0, MAX_RECENT_LOCATIONS)));
  } catch (e) {
    console.error('Failed to save recent locations:', e);
  }
};

/**
 * Load product names from localStorage
 * Maps productId -> productName for breadcrumb display
 */
const loadProductNames = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(PRODUCT_NAMES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Save product name mapping to localStorage
 */
const saveProductName = (productId: string, productName: string): void => {
  try {
    const existing = loadProductNames();
    existing[productId] = productName;
    localStorage.setItem(PRODUCT_NAMES_STORAGE_KEY, JSON.stringify(existing));
  } catch (e) {
    console.error('Failed to save product name:', e);
  }
};

/**
 * Get product name from localStorage
 */
const getStoredProductName = (productId: string): string | undefined => {
  const names = loadProductNames();
  return names[productId];
};

/**
 * Check if two locations are equal
 */
const locationsEqual = (a: DashboardLocation, b: DashboardLocation): boolean => {
  return (
    a.level === b.level &&
    a.productId === b.productId &&
    a.artifactId === b.artifactId
  );
};

// ============================================================================
// PROVIDER
// ============================================================================

interface DashboardNavigationProviderProps {
  children: ReactNode;
}

export const DashboardNavigationProvider: React.FC<DashboardNavigationProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [state, setState] = useState<DashboardNavigationState>(() => {
    const initialLocation = parseLocationFromPath(location.pathname);
    return {
      currentLocation: initialLocation,
      breadcrumbs: generateBreadcrumbs(initialLocation),
      favorites: loadFavorites(),
      recentLocations: loadRecentLocations(),
      isTransitioning: false,
    };
  });

  // Sync state with URL changes
  useEffect(() => {
    if (location.pathname.startsWith('/dashboard')) {
      setState(prev => {
        // Parse with existing location to preserve metadata
        const newLocation = parseLocationFromPath(location.pathname, prev.currentLocation);
        
        // Don't update if location hasn't changed
        if (locationsEqual(prev.currentLocation, newLocation) && 
            prev.currentLocation.productName === newLocation.productName) {
          return prev;
        }
        
        // Add to recent locations
        const newRecent = [
          newLocation,
          ...prev.recentLocations.filter(loc => !locationsEqual(loc, newLocation))
        ].slice(0, MAX_RECENT_LOCATIONS);
        saveRecentLocations(newRecent);
        
        return {
          ...prev,
          currentLocation: newLocation,
          breadcrumbs: generateBreadcrumbs(newLocation),
          recentLocations: newRecent,
          isTransitioning: false,
        };
      });
    }
  }, [location.pathname]);

  // Navigation actions
  const navigateTo = useCallback((newLocation: DashboardLocation) => {
    // Save productName to localStorage for future recovery
    if (newLocation.productId && newLocation.productName) {
      saveProductName(newLocation.productId, newLocation.productName);
    }
    
    // Update state first with the new location (including metadata)
    setState(prev => ({ 
      ...prev, 
      isTransitioning: true,
      currentLocation: newLocation,
      breadcrumbs: generateBreadcrumbs(newLocation),
    }));
    
    const path = getLocationPath(newLocation);
    navigate(path);
  }, [navigate]);

  const navigateToPath = useCallback((path: string) => {
    setState(prev => ({ ...prev, isTransitioning: true }));
    navigate(path);
  }, [navigate]);

  const goBack = useCallback(() => {
    const { breadcrumbs } = state;
    if (breadcrumbs.length > 1) {
      const previousLocation = breadcrumbs[breadcrumbs.length - 2].location;
      navigateTo(previousLocation);
    }
  }, [state, navigateTo]);

  const goToLevel = useCallback((level: DashboardLevel, artifactId?: string, artifactName?: string) => {
    navigateTo({ level, artifactId, artifactName });
  }, [navigateTo]);

  // Drill-down actions
  const drillDownToProduct = useCallback((productId: string, productName: string) => {
    navigateTo({ level: 'product', productId, productName });
  }, [navigateTo]);

  const drillDownToOperations = useCallback((productId: string, productName: string) => {
    navigateTo({ level: 'operations', productId, productName });
  }, [navigateTo]);

  const drillDownToDevelopment = useCallback((productId: string, productName: string) => {
    navigateTo({ level: 'development', productId, productName });
  }, [navigateTo]);

  const drillDownToProjections = useCallback((productId: string, productName: string) => {
    navigateTo({ level: 'projections', productId, productName });
  }, [navigateTo]);

  const drillDownToMarketFit = useCallback((productId: string, productName: string) => {
    navigateTo({ level: 'marketfit', productId, productName });
  }, [navigateTo]);

  const drillDownToStakeholders = useCallback(() => {
    navigateTo({ level: 'stakeholders' });
  }, [navigateTo]);

  const drillDownToCustomers = useCallback(() => {
    navigateTo({ level: 'customers' });
  }, [navigateTo]);

  // Favorites management
  const addFavorite = useCallback((label: string, loc?: DashboardLocation) => {
    const targetLocation = loc || state.currentLocation;
    const favorite: DashboardFavorite = {
      id: `fav-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      label,
      location: targetLocation,
      path: getLocationPath(targetLocation),
      createdAt: new Date().toISOString(),
    };
    
    setState(prev => {
      const newFavorites = [...prev.favorites, favorite];
      saveFavorites(newFavorites);
      return { ...prev, favorites: newFavorites };
    });
  }, [state.currentLocation]);

  const removeFavorite = useCallback((favoriteId: string) => {
    setState(prev => {
      const newFavorites = prev.favorites.filter(f => f.id !== favoriteId);
      saveFavorites(newFavorites);
      return { ...prev, favorites: newFavorites };
    });
  }, []);

  const isFavorite = useCallback((loc: DashboardLocation): boolean => {
    return state.favorites.some(f => locationsEqual(f.location, loc));
  }, [state.favorites]);

  const toggleFavorite = useCallback((label?: string) => {
    const { currentLocation, favorites } = state;
    const existingFavorite = favorites.find(f => locationsEqual(f.location, currentLocation));
    
    if (existingFavorite) {
      removeFavorite(existingFavorite.id);
    } else {
      const defaultLabel = label || LEVEL_LABELS[currentLocation.level];
      addFavorite(defaultLabel, currentLocation);
    }
  }, [state, removeFavorite, addFavorite]);

  // Utility functions
  const getCurrentPath = useCallback(() => {
    return getLocationPath(state.currentLocation);
  }, [state.currentLocation]);

  const contextValue: DashboardNavigationContextType = {
    ...state,
    navigateTo,
    navigateToPath,
    goBack,
    goToLevel,
    drillDownToProduct,
    drillDownToOperations,
    drillDownToDevelopment,
    drillDownToProjections,
    drillDownToMarketFit,
    drillDownToStakeholders,
    drillDownToCustomers,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    getLocationPath,
    getCurrentPath,
  };

  return (
    <DashboardNavigationContext.Provider value={contextValue}>
      {children}
    </DashboardNavigationContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useDashboardNavigation = (): DashboardNavigationContextType => {
  const context = useContext(DashboardNavigationContext);
  if (!context) {
    throw new Error('useDashboardNavigation must be used within a DashboardNavigationProvider');
  }
  return context;
};

// Export helper functions for use outside of context
export { getLocationPath, parseLocationFromPath, LEVEL_LABELS, LEVEL_ICONS };
